import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Users, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { teamsApi, PublicInvitationInfo } from '../lib/teamsApi';

export default function TeamAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { refreshUser, isAuthenticated, acceptInvite } = useAuth();
  const [info, setInfo] = useState<PublicInvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  // Beitritts-Formular (nur wenn nicht eingeloggt)
  const [pw, setPw] = useState('');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');

  useEffect(() => {
    if (!token) return;
    teamsApi.getInvitationByToken(token)
      .then(setInfo)
      .catch(e => setError(e instanceof Error ? e.message : 'Einladung nicht gefunden'))
      .finally(() => setLoading(false));
  }, [token]);

  // Eingeloggt: bestehende Annahme.
  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true); setError('');
    try {
      await teamsApi.acceptInvitation(token);
      await refreshUser();
      setDone(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Annehmen');
    } finally { setAccepting(false); }
  };

  // Nicht eingeloggt: Konto + Beitritt in einem Schritt (Passwort, optional Name).
  const handleJoin = async () => {
    if (!token) return;
    if (pw.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein.'); return; }
    setAccepting(true); setError('');
    try {
      await acceptInvite(token, { password: pw, vorname: vorname.trim() || undefined, nachname: nachname.trim() || undefined });
      setDone(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Beitritt fehlgeschlagen');
    } finally { setAccepting(false); }
  };

  const base = `min-h-screen flex items-center justify-center p-4 ${darkMode ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'}`;
  const card = `w-full max-w-md rounded-2xl shadow-lg p-8 ${darkMode ? 'bg-slate-800' : 'bg-white'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`;

  if (loading) return (
    <div className={base}><div className={card + ' flex justify-center'}><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div></div>
  );

  if (done) return (
    <div className={base}>
      <div className={`${card} text-center`}>
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold mb-2">Willkommen im Team!</h1>
        <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>Du wirst weitergeleitet…</p>
      </div>
    </div>
  );

  return (
    <div className={base}>
      <div className={card}>
        <div className="text-center mb-6">
          <div className="inline-flex p-3 bg-blue-600 rounded-xl mb-3"><Users className="w-7 h-7 text-white" /></div>
          <div className="text-base font-bold text-slate-900 dark:text-white leading-tight">Berater Digital</div>
          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-400'}`}>BAFA-Beratungssoftware</div>
        </div>

        {error && !info ? (
          <div className="text-center">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold mb-2">Einladung nicht gefunden</h1>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{error}</p>
            <Link to="/" className="text-blue-500 hover:underline text-sm">Zurück zur Startseite</Link>
          </div>
        ) : info?.alreadyAccepted ? (
          <div className="text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold mb-2">Bereits angenommen</h1>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Diese Einladung wurde bereits verwendet.</p>
            <Link to="/" className="text-blue-500 hover:underline text-sm">Zur Startseite</Link>
          </div>
        ) : info ? (
          <>
            <h1 className="text-xl font-bold text-center mb-1">Du wurdest eingeladen</h1>
            <p className={`text-center text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {info.inviterName} lädt dich ins Team ein
            </p>
            <div className={`rounded-xl p-4 mb-6 ${darkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
              <p className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Team</p>
              <p className="font-semibold text-base">{info.teamName}</p>
            </div>

            {error && <p className="text-red-500 text-sm mb-3 text-center">{error}</p>}

            {isAuthenticated ? (
              <button onClick={handleAccept} disabled={accepting}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Einladung annehmen
              </button>
            ) : (
              <div className="space-y-3">
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Passwort festlegen, um beizutreten.
                </p>
                <input type="password" autoComplete="new-password" placeholder="Passwort (min. 8 Zeichen)" value={pw} onChange={e => setPw(e.target.value)} className={inputCls} />
                <div className="flex gap-2">
                  <input type="text" placeholder="Vorname (optional)" value={vorname} onChange={e => setVorname(e.target.value)} className={inputCls} />
                  <input type="text" placeholder="Nachname (optional)" value={nachname} onChange={e => setNachname(e.target.value)} className={inputCls} />
                </div>
                <button onClick={handleJoin} disabled={accepting || pw.length < 8}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                  {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Beitreten
                </button>
              </div>
            )}
            <Link to="/" className={`block text-center text-sm mt-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'} hover:underline`}>Abbrechen</Link>
          </>
        ) : null}
      </div>
    </div>
  );
}
